/**
 * GenreLibrary.js
 * Source of Truth for Phase 4 Generative Logic.
 * Defines Ensemble Types and Sub-Genres with specific musical traits.
 */

import { RANGE_PRESETS, MELODIC_PROFILE_PRESETS, ROLE_BEHAVIORS } from './StrategyConfig.js';

export const GENRE_LIBRARY = {
    // -------------------------------------------------------------------------
    // ENSEMBLE 1: FULL ORCHESTRA
    // Target Output: Cinematic, Classical, and Theatrical scores.
    // -------------------------------------------------------------------------
    orchestra: {
        name: "Full Orchestra",
        ensembleConstraint: { min: 10, max: 16, requiredRoles: ['melody', 'bass', 'percussion', 'harmony'] },
        defaults: {
            mixProfile: {
                masterBus: {
                    compression: { threshold: -15, ratio: 2, attack: 0.03, release: 0.2 },
                    eq: { low: 0, mid: -1, high: 1 }, // Subtle smile
                    width: 0.7 // Natural width
                },
                globalReverb: { type: 'hall', decay: 2.5, preDelay: 0.02, wet: 0.3 },
                globalDelay: { delayTime: 0.3, feedback: 0.3, wet: 0.1 }
            },
            bpm: 80,
            key: 'C',
            scale: 'minor',
            structure: [
                { type: 'INTRO', bars: 4 },
                { type: 'BUILD', bars: 8 },
                { type: 'CLIMAX', bars: 8 },
                { type: 'OUTRO', bars: 4 }
            ],
            // Default Strictness: Cinematic (Allows power chords, specific doublings)
            voiceLeadingStrictness: 'cinematic',
            doublingStrategy: 'cinematic',
            harmonicRhythm: 'moderate',
            chordComplexity: 'triad',
            rubatoIntensity: 0.0,
            syncopationLevel: 20, // Low for orchestra
            voicingProfile: { spread: [0, 12, 19, 24], doublings: ['root', 'fifth'] },
            instrumentationProfile: 'orchestra_standard',
            dynamicCurve: 'swell'
        },
        subGenres: {
            epic_cinematic: {
                name: "Epic Cinematic",
                defaults: { bpm: 90, key: 'D', scale: 'minor' },
                harmonicRhythm: 'slow_burn',
                chordComplexity: 'triad',
                voicingProfile: { spread: [0, 7, 12, 24], doublings: ['root', 'fifth', 'root'] },
                percussionProfile: 'taiko_heavy',
                rhythm: "Heavy triplets, Spiccato ostinatos.",
                harmony: "Minor/Phrygian. Power chords + Octaves.",
                voicing: "Wide. Low Brass roots, High String tremolos.",
                velocity: "Max velocity on downbeats. Huge swells.",
                velocityStrategy: "dynamic_swell",
                melodyStrategy: ['lyrical', 'motific', 'call_and_response'], // Variance: Allow multiple valid strategies
                intervalProfile: { step: 0.8, skip: 0.15, leap: 0.05 }, // Boosted Step Ratio for Lyrical Flow
                tessitura: { low: 72, high: 96, preferred_center: 84 }, // Force High Melody
                logicTags: ["swell(slow)", "percussion(taiko)"],
                voiceLeadingStrictness: 'cinematic',
                doublingStrategy: 'power',
                roleCriteria: {
                    melody: RANGE_PRESETS.epic_melody, // Use new high range
                    harmony_rhythmic: RANGE_PRESETS.epic_rhythmic,
                    harmony_color: RANGE_PRESETS.high_color,
                    harmony: RANGE_PRESETS.epic_harmony,
                    harmony_pad: RANGE_PRESETS.spectral_low // Force Pads Low to balance High Energy
                },
                melodicRules: MELODIC_PROFILE_PRESETS.epic_lyrical,
                roleBehaviors: {
                    harmony_rhythmic: ROLE_BEHAVIORS.epic_ostinato,
                    bass: ROLE_BEHAVIORS.epic_bass
                },
                forcedRoles: [
                    { instrumentId: 'contrabass', role: 'bass', strategy: 'monophonic_root', rhythmStrategy: 'rhythmic_comp', ifMissing: true }, // Pulse to increase Low density
                    { instrumentId: 'strings', role: 'harmony_pad', ifMissing: false },
                    { instrumentId: 'harp', role: 'harmony_color', ifMissing: true } // Ensure High arpeggios
                ]
            },
            romantic: {
                name: "Romantic",
                defaults: { bpm: 68, key: 'Eb', scale: 'major' },
                rubatoIntensity: 0.8,
                chordComplexity: 'extended',
                harmonicRhythm: 'moderate',
                melodicContour: 'sweeping_arch',
                rhythm: "Rubato (fluid time). No rigid grid.",
                harmony: "Major/Lydian. Rich extensions (9ths, 11ths).",
                voicing: "Lush. Close voicing in strings, warm horns.",
                velocity: "Expressive. Long crescendos.",
                velocityStrategy: "dynamic_swell",
                melodyStrategy: ['lyrical', 'motific', 'counterpoint', 'virtuoso_solo'], // Variance enabled
                logicTags: ["humanize(max)", "legato(on)"],
                voiceLeadingStrictness: 'strict',
                doublingStrategy: 'classic',
                melodicRules: MELODIC_PROFILE_PRESETS.romantic,
                roleCriteria: {
                    melody: { min: 67 }, // Tuned to G4 to push into High bucket (>70) while allowing some warmth
                    harmony_color: RANGE_PRESETS.fantasy_harmony
                }
            },
            baroque: {
                name: "Baroque",
                defaults: { bpm: 110, key: 'G', scale: 'major', timeSig: 4 },
                harmonicRhythm: 'moderate', // 1 chord per bar (Target 1.0)
                chordComplexity: 'triad',
                rubatoIntensity: 0.1,
                rhythm: "Motorik 1/16ths. Steady pulse.",
                harmony: "Functional Harmony. Circle of 5ths.",
                voicing: "Counterpoint. Independent lines. Harpsichord texture.",
                velocity: "Terraced dynamics (sudden loud/soft).",
                velocityStrategy: "terraced",
                melodyStrategy: ['counterpoint', 'motific', 'sequence'],
                tessitura: { low: 72, high: 96, preferred_center: 84 },
                logicTags: ["ornamentation(trills)", "polyphony(high)"],
                voiceLeadingStrictness: 'strict',
                doublingStrategy: 'classic',
                melodicRules: MELODIC_PROFILE_PRESETS.baroque,
                roleCriteria: {
                    melody: RANGE_PRESETS.baroque_melody,
                    harmony_color: RANGE_PRESETS.baroque_harmony_color
                },
                roleBehaviors: {
                    bass: ROLE_BEHAVIORS.baroque_bass
                }
            },
            horror_tension: {
                name: "Horror / Tension",
                defaults: { bpm: 60, key: 'F#', scale: 'locrian' },
                harmonicRhythm: 'static',
                chordComplexity: 'cluster',
                rhythm: "Irregular. Polyrhythms. Silence.",
                harmony: "Atonal, Chromatic, Locrian. Clusters.",
                voicing: "Extreme ranges (screeching highs, rumble lows).",
                velocity: "Random spikes. Sudden 'Stings'.",
                velocityStrategy: "terraced",
                // Phase 4.5 Audit: Removed hard 'max' constraints to allow mutation influence.
                logicTags: ["dissonance(high)", "staccato(random)", "chaos(high)"],
                voiceLeadingStrictness: 'loose',
                doublingStrategy: 'cinematic',
                roleCriteria: {
                    harmony: RANGE_PRESETS.horror_harmony,
                    harmony_pad: RANGE_PRESETS.horror_harmony,
                    harmony_rhythmic: RANGE_PRESETS.horror_harmony,
                    harmony_color: RANGE_PRESETS.horror_harmony
                },
                roleBehaviors: {
                    harmony_pad: ROLE_BEHAVIORS.horror_cloud,
                    harmony_rhythmic: { strategy: 'aleatoric_cloud', rhythmStrategy: 'stabs' },
                    harmony_color: { strategy: 'swarm', rhythmStrategy: 'swarm' }
                }
            },
            minimalist: {
                name: "Minimalist",
                defaults: { bpm: 130, key: 'A', scale: 'minor' },
                harmonicRhythm: 'static',
                chordComplexity: 'triad',
                rhythm: "Repetitive 1/8th pulse.",
                harmony: "Static harmony. Slow changes over time.",
                voicing: "Arpeggiated patterns. Interlocking layers.",
                velocity: "Very flat/consistent.",
                velocityStrategy: "flat",
                tessitura: { low: 48, high: 72, preferred_center: 60 }, // Force Mid-range (C3-C5)
                logicTags: ["repetition(high)", "evolution(slow)"],
                voiceLeadingStrictness: 'strict',
                doublingStrategy: 'classic',
                roleCriteria: {
                    melody: { min: 48, max: 72 }, // Keep melody in Mid
                    harmony: { min: 48, max: 72 },
                    harmony_rhythmic: { min: 48, max: 72 }
                }
            },
            impressionist: {
                name: "Impressionist",
                defaults: { bpm: 72, key: 'Db', scale: 'whole_tone' }, // Suggest Whole Tone default
                rubatoIntensity: 0.6,
                chordComplexity: 'extended',
                harmonicRhythm: 'moderate', // Force 1 chord/bar
                rhythm: "Fluid, obscure pulse.",
                harmony: "Whole tone, Pentatonic. Parallel chords.",
                voicing: "Blurry. Sustained pedals. Woodwind flourishes.",
                velocity: "Soft, misty, low velocity.",
                velocityStrategy: "soft_mist",
                tessitura: { low: 72, high: 96, preferred_center: 84 }, // Force High Melody (Flutes/Violins)
                logicTags: ["sustain(high)", "blur(on)"],
                voiceLeadingStrictness: 'cinematic',
                doublingStrategy: 'classic',
                melodicRules: MELODIC_PROFILE_PRESETS.impressionist
            },
            fantasy: {
                name: "Fantasy",
                defaults: { timeSig: 3, bpm: 96, key: 'F', scale: 'lydian' },
                rhythm: "3/4 or 6/8 Waltz feel.",
                harmony: "Lydian/Dorian. Magical transitions.",
                voicing: "Harp glissandos. Flute melodies.",
                velocity: "Gentle, magical swells.",
                velocityStrategy: "dynamic_swell",
                logicTags: ["glissando(often)", "magic(on)"],
                voiceLeadingStrictness: 'cinematic',
                doublingStrategy: 'classic',
                roleCriteria: {
                     harmony_color: RANGE_PRESETS.fantasy_harmony,
                     harmony_rhythmic: RANGE_PRESETS.fantasy_harmony
                },
                forcedRoles: [
                    { instrumentId: 'harp', role: 'harmony_color', strategy: 'arpeggio', ifMissing: true },
                    { instrumentId: 'flute', role: 'counter_melody', ifMissing: true }
                ]
            },
            action_chase: {
                name: "Action/Chase",
                defaults: { timeSig: 5, bpm: 150, key: 'C', scale: 'minor' },
                // Force high rhythmic density for mid-range energy
                rhythmicDensity: 0.9,
                harmonicRhythm: 'fast',
                chordComplexity: 'power',
                rhythm: "Fast 7/8 or 5/4. Driving snare.",
                harmony: "Minor/Diminished. Chromatic descents.",
                voicing: "Stabs. Brass/Percussion focus.",
                velocity: "Aggressive. Hard attacks.",
                velocityStrategy: "dynamic_swell",
                melodyStrategy: ['ostinato', 'riff', 'motific'], // Variance
                logicTags: ["tempo(fast)", "accents(heavy)"],
                voiceLeadingStrictness: 'loose',
                doublingStrategy: 'power',
                roleCriteria: {
                    melody: RANGE_PRESETS.action_melody,
                    harmony_pad: RANGE_PRESETS.action_pad,
                    harmony_rhythmic: RANGE_PRESETS.action_rhythmic
                },
                roleBehaviors: {
                    harmony_rhythmic: { scoreBonus: 0.3, requiredFamily: 'brass' }
                },
                forcedRoles: [
                    { instrumentId: 'contrabass', role: 'bass', strategy: 'rhythmic', ifMissing: true },
                    { instrumentId: 'timpani', role: 'percussion', ifMissing: true }
                ]
            },
            neo_classical: {
                name: "Neo-Classical",
                defaults: { bpm: 125, key: 'A', scale: 'harmonic_minor' },
                harmonicRhythm: 'fast',
                chordComplexity: 'triad',
                rhythm: "Strict, mechanical grid.",
                harmony: "Tonal but modern. Triads.",
                voicing: "Alberti Bass. Clean separation.",
                velocity: "Moderate, clear.",
                velocityStrategy: "flat",
                voicingStrategy: "alberti",
                logicTags: ["clarity(high)", "dry(on)"],
                voiceLeadingStrictness: 'strict',
                doublingStrategy: 'classic'
            },
            ethereal: {
                name: "Ethereal",
                defaults: { bpm: 60, key: 'B', scale: 'major' },
                rubatoIntensity: 0.4,
                harmonicRhythm: 'slow_burn',
                chordComplexity: 'extended',
                rhythm: "Free time. No pulse.",
                harmony: "Major 7ths, Suspended chords.",
                voicing: "Choir/Pad focus. Slow attack strings.",
                velocity: "Very soft. 'Ghost' notes.",
                velocityStrategy: "soft_mist",
                logicTags: ["attack(slow)", "reverb(wet)"],
                voiceLeadingStrictness: 'cinematic',
                doublingStrategy: 'classic'
            },
            gothic: {
                name: "Gothic",
                defaults: { bpm: 55, key: 'C', scale: 'harmonic_minor' },
                rhythm: "Heavy, slow march.",
                harmony: "Minor Harmonic. Organ tones.",
                voicing: "Thick, dark low-end.",
                velocity: "Heavy, dragging feel.",
                velocityStrategy: "heavy",
                logicTags: ["mood(dark)", "bass(heavy)"],
                voiceLeadingStrictness: 'cinematic',
                doublingStrategy: 'power'
            },
            sci_fi: {
                name: "Sci-Fi",
                defaults: { bpm: 110, key: 'Bb', scale: 'augmented' },
                rhythm: "Unpredictable. Glitchy.",
                harmony: "Augmented chords. Synthetic scales.",
                voicing: "Metallic textures. Sul Ponticello strings.",
                velocity: "Cold, robotic.",
                velocityStrategy: "flat",
                melodyStrategy: 'ostinato',
                logicTags: ["texture(metallic)", "interval(wide)"],
                voiceLeadingStrictness: 'loose',
                doublingStrategy: 'cinematic',
                melodicRules: { ostinatoVariant: 'scifi' }
            },
            pastoral: {
                name: "Pastoral",
                defaults: { bpm: 84, key: 'F', scale: 'pentatonic_major', timeSig: 3 }, // Often 6/8
                rhythm: "Gentle 6/8. Folk-like.",
                harmony: "Major/Pentatonic. Simple triads.",
                voicing: "Woodwind leads (Oboe/Clarinet). Light strings.",
                velocity: "Gentle, flowing.",
                velocityStrategy: "dynamic_swell",
                logicTags: ["mood(peaceful)", "instrument(woodwind)"],
                voiceLeadingStrictness: 'classic',
                doublingStrategy: 'classic'
            },
            avant_garde: {
                name: "Avant-Garde",
                defaults: { bpm: 90, key: 'C', scale: 'chromatic' }, // effectively atonal
                rhythm: "Random/Stochastic.",
                harmony: "12-Tone rows. Microtonal.",
                voicing: "Pointillism (isolated notes).",
                velocity: "Extreme variance (pp to ff).",
                velocityStrategy: "random_spike",
                logicTags: ["chaos(high)", "melody(fragmented)"],
                voiceLeadingStrictness: 'loose',
                doublingStrategy: 'loose'
            },
            waltz: {
                name: "Waltz",
                defaults: { timeSig: 3, bpm: 120, key: 'D', scale: 'major' },
                rhythm: "Strict 3/4 (Boom-Chick-Chick).",
                harmony: "Major/Minor. Functional harmony.",
                voicing: "Bass on 1, Chords on 2 & 3.",
                velocity: "Emphasis on beat 1.",
                velocityStrategy: "accent_downbeat",
                logicTags: ["time_sig(3/4)", "dance(on)"],
                voiceLeadingStrictness: 'classic',
                doublingStrategy: 'classic'
            },
            cyber_orch: {
                name: "Cyber-Orch",
                defaults: { bpm: 140, key: 'F', scale: 'phrygian' },
                rhythm: "Hybrid Electronic/Orchestral.",
                harmony: "Minor. Synths doubling strings.",
                voicing: "Unison lines. Powerful bass.",
                velocity: "Hard, quantized.",
                velocityStrategy: "fixed_max",
                logicTags: ["hybrid(on)", "power(high)"],
                voiceLeadingStrictness: 'cinematic',
                doublingStrategy: 'power',
                forcedRoles: [
                     { instrumentId: 'pad', role: 'harmony', ifMissing: true },
                     { instrumentId: 'bass_synth', role: 'bass', ifMissing: true }
                ]
            },
            medieval: {
                name: "Medieval",
                defaults: { bpm: 100, key: 'D', scale: 'dorian' },
                rhythm: "Open rhythm. Drone based.",
                harmony: "Dorian/Mixolydian. Open 5ths.",
                voicing: "Rebec/Lute emulation. No vibrato.",
                velocity: "Flat, folk-like.",
                velocityStrategy: "flat",
                logicTags: ["harmony(open)", "drone(on)"],
                voiceLeadingStrictness: 'loose', // Parallel 5ths are part of the style
                doublingStrategy: 'classic'
            },
            noire: {
                name: "Noire",
                defaults: { bpm: 60, key: 'Am', scale: 'harmonic_minor' }, // Jazz Minor?
                rubatoIntensity: 0.4,
                chordComplexity: 'jazz',
                rhythm: "Slow Jazz drag.",
                harmony: "Minor with Major 6ths.",
                voicing: "Muted trumpet, low strings.",
                velocity: "Smoky, lazy.",
                velocityStrategy: "soft_mist",
                logicTags: ["mood(mystery)", "tempo(slow)"],
                voiceLeadingStrictness: 'cinematic',
                doublingStrategy: 'classic',
                forcedRoles: [
                    { instrumentId: 'trumpet', role: 'melody', strategy: 'noir_solo', ifMissing: true }
                ]
            },
            christmas: {
                name: "Christmas",
                defaults: { bpm: 130, key: 'G', scale: 'major' },
                rhythm: "Sleigh-bell rhythm (4/4 shuffles).",
                harmony: "Major. Diminished passing chords.",
                voicing: "Glockenspiel/Celeste heavy.",
                velocity: "Bright, cheery.",
                velocityStrategy: "accent_downbeat",
                logicTags: ["bells(on)", "mood(happy)"],
                voiceLeadingStrictness: 'classic',
                doublingStrategy: 'classic',
                forcedRoles: [
                    { instrumentId: 'celesta', role: 'melody', strategy: 'section_aware', ifMissing: true }
                ]
            },
            western: {
                name: "Western",
                defaults: { bpm: 110, key: 'A', scale: 'major' },
                rhythm: "Gallop rhythm (1/8th + 2/16ths).",
                harmony: "Major. Copland-esque open harmony.",
                voicing: "Solo Trumpet/Violin.",
                velocity: "Bold, heroic.",
                velocityStrategy: "fixed_max",
                logicTags: ["style(americana)"],
                voiceLeadingStrictness: 'cinematic',
                doublingStrategy: 'classic'
            }
        }
    },

    // -------------------------------------------------------------------------
    // ENSEMBLE 2: ROCK BAND
    // Target Output: Electric Guitar, Bass, Drums, Keys.
    // -------------------------------------------------------------------------
    rock_band: {
        name: "Rock Band",
        ensembleConstraint: { min: 3, max: 5, requiredRoles: ['bass', 'percussion', 'melody'] },
        defaults: {
            mixProfile: {
                masterBus: {
                    compression: { threshold: -12, ratio: 4, attack: 0.01, release: 0.1 }, // Punchy
                    eq: { low: 2, mid: 1, high: 2 }, // V-shape
                    width: 0.6
                },
                globalReverb: { type: 'plate', decay: 1.2, preDelay: 0.01, wet: 0.15 },
                globalDelay: { delayTime: '8n.', feedback: 0.25, wet: 0.1 }
            },
            bpm: 120,
            key: 'E',
            scale: 'minor', // Rock often uses minor pentatonic context
            structure: [
                { type: 'INTRO', bars: 4 },
                { type: 'VERSE', bars: 8 },
                { type: 'CHORUS', bars: 8 },
                { type: 'VERSE', bars: 8 },
                { type: 'CHORUS', bars: 8 },
                { type: 'OUTRO', bars: 4 }
            ],
            harmonicRhythm: 'moderate',
            chordComplexity: 'power',
            rubatoIntensity: 0.0,
            syncopationLevel: 40,
            riffStyle: 'power_chord_chug',
            groovePocket: 'center',
            voiceLeadingStrictness: 'loose',
            doublingStrategy: 'power'
        },
        subGenres: {
            classic_rock: {
                name: "Classic Rock",
                defaults: { bpm: 115, key: 'A', scale: 'mixolydian' },
                riffStyle: 'pentatonic_box',
                groovePocket: 'behind', // Laid back feel
                rhythm: "Standard 4/4 backbeat.",
                harmony: "Mixolydian. Power chords (1-5).",
                voicing: "Open guitar chords. Bluesy licks.",
                velocity: "Medium hard. 'In the pocket.'",
                velocityStrategy: "groove",
                melodyStrategy: ['riff', 'call_and_response', 'motific'],
                logicTags: ["groove(standard)", "blues_licks(on)"]
            },
            punk: {
                name: "Punk",
                defaults: { bpm: 170, key: 'E', scale: 'major' },
                harmonicRhythm: 'fast',
                chordComplexity: 'power',
                syncopationLevel: 10, // Straight 8ths
                riffStyle: 'power_chord_chug',
                rhythm: "Fast 1/8th downstrokes.",
                harmony: "Major/Minor triads. 3-Chord songs.",
                voicing: "Barre chords only. Root bass.",
                velocity: "Max velocity constant.",
                velocityStrategy: "fixed_max",
                melodyStrategy: ['riff', 'power_chord'],
                logicTags: ["velocity(127)", "humanize(low)"]
            },
            math_rock: {
                name: "Math Rock",
                defaults: { timeSig: 7, bpm: 130, key: 'F', scale: 'lydian' },
                harmonicRhythm: 'hyper',
                chordComplexity: 'extended',
                rhythm: "Odd time (5/4, 7/8). Polyrhythms.",
                harmony: "Extended chords (maj9, add9).",
                voicing: "Tapping licks. Clean tone.",
                velocity: "Highly accented grid.",
                velocityStrategy: "accent_grid",
                melodyStrategy: ['counterpoint', 'motific', 'riff'],
                logicTags: ["time_sig(complex)", "clean(on)"]
            },
            grunge: {
                name: "Grunge",
                defaults: { bpm: 85, key: 'D', scale: 'minor' }, // Drop D feel
                rhythm: "Sludgey, behind beat.",
                harmony: "Chromatic shifts. Drop D power.",
                voicing: "Muddy, low-mid focus.",
                velocity: "Quiet verse, LOUD chorus.",
                velocityStrategy: "dynamic_swell", // Uses section types
                melodyStrategy: ['riff', 'lyrical'],
                logicTags: ["dynamics(extreme)", "distortion(high)"]
            },
            surf_rock: {
                name: "Surf Rock",
                defaults: { bpm: 160, key: 'E', scale: 'phrygian_dominant' }, // Misirlou scale
                rhythm: "Fast 1/16th picking (tremolo).",
                harmony: "Major/Phrygian Dominant.",
                voicing: "Reverb-drenched guitar leads.",
                velocity: "Consistent, rapid.",
                velocityStrategy: "fixed_max",
                logicTags: ["picking(tremolo)", "reverb(spring)"]
            },
            metalcore: {
                name: "Metalcore",
                defaults: { bpm: 130, key: 'C', scale: 'phrygian' }, // Often Drop C
                rhythm: "Breakdown rhythms (0-0-0) Metal Blast.",
                harmony: "Phrygian/Locrian. Drop tuning.",
                voicing: "Palm mutes. Dissonant intervals.",
                velocity: "Machine-gun tight.",
                velocityStrategy: "fixed_max",
                melodyStrategy: ['riff', 'breakdown'],
                logicTags: ["palm_mute(on)", "breakdown(often)"]
            },
            shoegaze: {
                name: "Shoegaze",
                defaults: { bpm: 100, key: 'Eb', scale: 'major' },
                harmonicRhythm: 'slow_burn',
                chordComplexity: 'extended',
                rhythm: "Wall of sound. Washed out.",
                harmony: "Major 7ths. Dream pop.",
                voicing: "Strumming heavily. Buried vocals.",
                velocity: "Consistent wash.",
                velocityStrategy: "wash",
                melodyStrategy: ['wash', 'drone'],
                logicTags: ["wash(high)", "feedback(on)"]
            },
            blues_rock: {
                name: "Blues Rock",
                defaults: { bpm: 90, key: 'E', scale: 'blues', timeSig: 12 }, // 12/8 shuffle
                rhythm: "Shuffle feel (12/8).",
                harmony: "Blues Scale (b3, b5, b7).",
                voicing: "Call and response.",
                velocity: "Expressive bending.",
                velocityStrategy: "groove",
                melodyStrategy: ['riff', 'call_and_response', 'blues_licks'],
                logicTags: ["swing(heavy)", "scale(blues)"]
            },
            indie_pop: {
                name: "Indie Pop",
                defaults: { bpm: 135, key: 'A', scale: 'lydian' },
                rhythm: "Upbeat disco-beats (hi-hats).",
                harmony: "Major/Lydian. Jangly chords.",
                voicing: "Highlife guitar picking.",
                velocity: "Light, bouncy.",
                velocityStrategy: "bouncy",
                melodyStrategy: ['lyrical', 'motific'],
                logicTags: ["mood(happy)", "rhythm(dance)"]
            },
            post_rock: {
                name: "Post-Rock",
                defaults: { bpm: 75, key: 'D', scale: 'mixolydian' },
                rhythm: "Slow build. Repetitive.",
                harmony: "Major/Minor. Simple progressions.",
                voicing: "Tremolo picking. Delay layers.",
                velocity: "Gradual 4-minute crescendo.",
                velocityStrategy: "crescendo",
                melodyStrategy: ['motific', 'slow_build'],
                logicTags: ["build(slow)", "delay(heavy)"]
            },
            industrial: {
                name: "Industrial",
                defaults: { bpm: 110, key: 'C#', scale: 'chromatic' },
                rhythm: "Mechanical 4/4. Loops.",
                harmony: "Chromatic. Tritones.",
                voicing: "Synth-bass + Distorted Guitar.",
                velocity: "Robotic consistency.",
                velocityStrategy: "flat",
                melodyStrategy: ['ostinato', 'riff'],
                logicTags: ["humanize(none)", "texture(harsh)"]
            },
            funk_rock: {
                name: "Funk Rock",
                defaults: { bpm: 105, key: 'E', scale: 'dorian' },
                syncopationLevel: 90, // High syncopation
                riffStyle: 'single_note_groove',
                rhythm: "1/16th note scratch. Syncopated.",
                harmony: "Dorian/Mixolydian. 7th/9th chords.",
                voicing: "'Wah' patterns. Slap bass.",
                velocity: "Percussive ghost notes.",
                velocityStrategy: "ghost_heavy",
                melodyStrategy: ['riff', 'percussive'],
                logicTags: ["ghost_notes(high)", "bass(slap)"]
            },
            pop_punk: {
                name: "Pop Punk",
                defaults: { bpm: 155, key: 'B', scale: 'major' },
                rhythm: "Fast 4/4. Double time drums.",
                harmony: "Major Scale (I-V-vi-IV).",
                voicing: "Octaves melodies.",
                velocity: "High energy.",
                velocityStrategy: "fixed_max",
                melodyStrategy: ['lyrical', 'riff'],
                logicTags: ["tempo(fast)", "vibe(nostalgic)"]
            },
            stoner_doom: {
                name: "Stoner/Doom",
                defaults: { bpm: 55, key: 'C', scale: 'pentatonic_minor' },
                rhythm: "Super slow.",
                harmony: "Minor Pentatonic.",
                voicing: "Fuzz heavy. Bass leads.",
                velocity: "Dragging heavily.",
                velocityStrategy: "heavy",
                logicTags: ["tempo(slow)", "fuzz(max)"]
            },
            rockabilly: {
                name: "Rockabilly",
                defaults: { bpm: 160, key: 'E', scale: 'blues' },
                rhythm: "Swing/Shuffle.",
                harmony: "Blues structure.",
                voicing: "Slap-back echo. Walking bass.",
                velocity: "Bouncy.",
                velocityStrategy: "bouncy",
                logicTags: ["bass(walking)", "vibe(retro)"]
            },
            psychedelic: {
                name: "Psychedelic",
                defaults: { bpm: 115, key: 'A', scale: 'mixolydian' },
                rhythm: "Flowing, loose.",
                harmony: "Mixolydian/Dorian. Drone notes.",
                voicing: "Sitar-like lines. Organ pads.",
                velocity: "Swirling.",
                velocityStrategy: "dynamic_swell",
                melodyStrategy: ['motific', 'drone'],
                logicTags: ["effects(phaser)", "mood(trippy)"]
            },
            nu_metal: {
                name: "Nu-Metal",
                defaults: { bpm: 95, key: 'B', scale: 'minor' },
                rhythm: "Hip-hop groove + Heavy Guitars.",
                harmony: "Minor. 7-string range.",
                voicing: "Bouncy riffs. Turntables.",
                velocity: "Heavy groove.",
                velocityStrategy: "groove",
                melodyStrategy: ['riff', 'rhythmic'],
                logicTags: ["groove(bounce)", "tuning(low)"]
            },
            emo: {
                name: "Emo",
                defaults: { bpm: 140, key: 'Eb', scale: 'major' },
                rhythm: "4/4 but melodic.",
                harmony: "Major/Minor. Arpeggiated cleans.",
                voicing: "Twinkly guitars.",
                velocity: "Dynamic shifts.",
                velocityStrategy: "dynamic_swell",
                melodyStrategy: ['lyrical', 'counterpoint'],
                logicTags: ["mood(sad)", "arpeggio(clean)"]
            },
            glam_rock: {
                name: "Glam Rock",
                defaults: { bpm: 128, key: 'E', scale: 'major' },
                rhythm: "Stomp-clap beat.",
                harmony: "Major. Boogie-woogie.",
                voicing: "Anthemic choruses. Saxophones.",
                velocity: "Theatrical.",
                velocityStrategy: "accent_downbeat",
                melodyStrategy: ['call_and_response', 'anthem'],
                logicTags: ["anthem(on)", "beat(stomp)"]
            },
            progressive: {
                name: "Progressive",
                defaults: { bpm: 132, key: 'F#', scale: 'dorian', timeSig: 7 },
                harmonicRhythm: 'fast',
                chordComplexity: 'extended',
                rhythm: "Changing meters.",
                harmony: "Modal interchange. Virtuosic.",
                voicing: "Unison runs. Keyboard solos.",
                velocity: "Technical precision.",
                velocityStrategy: "accent_grid",
                melodyStrategy: ['virtuoso_solo', 'motific', 'counterpoint'],
                logicTags: ["complexity(high)", "solos(frequent)"]
            }
        }
    },

    // -------------------------------------------------------------------------
    // ENSEMBLE 3: JAZZ BAND
    // Target Output: Piano, Upright Bass, Drums, Brass, Sax.
    // -------------------------------------------------------------------------
    jazz_combo: {
        name: "Jazz Band",
        ensembleConstraint: { min: 3, max: 6, requiredRoles: ['bass', 'percussion'] },
        defaults: {
            mixProfile: {
                masterBus: {
                    compression: { threshold: -10, ratio: 2, attack: 0.05, release: 0.3 }, // Transparent
                    eq: { low: 0, mid: 2, high: 0 }, // Mid-range focus
                    width: 0.6
                },
                globalReverb: { type: 'room', decay: 0.8, preDelay: 0.01, wet: 0.1 },
                globalDelay: { delayTime: '4n', feedback: 0.1, wet: 0.05 }
            },
            bpm: 140,
            key: 'Bb',
            scale: 'dorian',
            structure: [
                { type: 'HEAD', bars: 8 },
                { type: 'SOLO_1', bars: 8 },
                { type: 'SOLO_2', bars: 8 },
                { type: 'HEAD', bars: 8 }
            ],
            harmonicRhythm: 'fast',
            chordComplexity: 'jazz',
            rubatoIntensity: 0.3,
            syncopationLevel: 70, // Swing default
            voicingProfile: { spread: [0, 3, 7, 10], doublings: [] }, // 7ths
            improvisationProfile: { scaleDensity: 'medium', chromaticism: 'medium' },
            voiceLeadingStrictness: 'loose', // Jazz follows different rules
            doublingStrategy: 'classic'
        },
        subGenres: {
            swing: {
                name: "Swing",
                defaults: { bpm: 160, key: 'Bb', scale: 'mixolydian' },
                harmonicRhythm: 'fast',
                compingPattern: 'charleston', // Charleston rhythm
                rhythm: "Standard triplet swing.",
                harmony: "ii-V-I. Extensions (9, 13).",
                voicing: "Walking bass. Comping piano.",
                velocity: "Accents on 2 and 4.",
                velocityStrategy: "swing_accents",
                melodyStrategy: ['motific', 'call_and_response', 'virtuoso_solo'],
                logicTags: ["swing(standard)", "bass(walk)"]
            },
            bebop: {
                name: "Bebop",
                defaults: { bpm: 220, key: 'F', scale: 'bebop' }, // Needs scale alias?
                harmonicRhythm: 'hyper',
                chordComplexity: 'jazz',
                improvisationProfile: { scaleDensity: 'high', chromaticism: 'high' },
                rhythm: "Fast, frantic.",
                harmony: "Chromatic approach notes.",
                voicing: "Complex heads. Solo focus.",
                velocity: "Hard, articulate.",
                velocityStrategy: "hard_bop",
                melodyStrategy: ['virtuoso_solo', 'motific'],
                logicTags: ["tempo(fast)", "notes(dense)"]
            },
            cool_jazz: {
                name: "Cool Jazz",
                defaults: { bpm: 90, key: 'Eb', scale: 'dorian' },
                improvisationProfile: { scaleDensity: 'low', chromaticism: 'medium' },
                rhythm: "Laid back, behind beat.",
                harmony: "Modal. Quartal voicings.",
                voicing: "Soft brushes on drums.",
                velocity: "Low, smooth.",
                velocityStrategy: "soft_mist",
                voicingStrategy: "quartal",
                melodyStrategy: ['lyrical', 'motific'],
                logicTags: ["mood(chill)", "instruments(muted)"]
            },
            bossa_nova: {
                name: "Bossa Nova",
                defaults: { bpm: 130, key: 'C', scale: 'major' },
                rhythm: "Straight 8ths / Clave.",
                harmony: "Major/Min 9ths. Altered 5ths.",
                voicing: "Guitar/Piano syncopation.",
                velocity: "Gentle, whispering.",
                velocityStrategy: "soft_mist",
                melodyStrategy: ['lyrical', 'motific'],
                logicTags: ["rhythm(latin)", "mood(relax)"]
            },
            hard_bop: {
                name: "Hard Bop",
                defaults: { bpm: 140, key: 'Ab', scale: 'blues' },
                rhythm: "Blues/Gospel influence.",
                harmony: "Dominant 7ths. Soulful.",
                voicing: "Brass harmony stabs.",
                velocity: "Driving, heavy swing.",
                velocityStrategy: "hard_bop",
                logicTags: ["vibe(soulful)", "energy(high)"]
            },
            free_jazz: {
                name: "Free Jazz",
                defaults: { bpm: 180, key: 'C', scale: 'chromatic' }, // No key really
                harmonicRhythm: 'hyper',
                chordComplexity: 'cluster',
                rubatoIntensity: 0.0,
                improvisationProfile: { scaleDensity: 'max', chromaticism: 'max' },
                rhythm: "No grid. Chaos.",
                harmony: "Atonal. No key center.",
                voicing: "Squeaks, honks, clusters.",
                velocity: "Random bursts.",
                velocityStrategy: "random_spike",
                melodyStrategy: ['chaos', 'free_improv'],
                logicTags: ["chaos(max)", "structure(none)"]
            },
            fusion: {
                name: "Fusion",
                defaults: { bpm: 110, key: 'G', scale: 'mixolydian' },
                rhythm: "Straight rock beat.",
                harmony: "Jazz harmony over Rock rhythm.",
                voicing: "Electric piano, Synth leads.",
                velocity: "Tight, funky.",
                velocityStrategy: "groove",
                melodyStrategy: ['virtuoso_solo', 'riff'],
                logicTags: ["hybrid(rock)", "instrument(electric)"]
            },
            gypsy_jazz: {
                name: "Gypsy Jazz",
                defaults: { bpm: 200, key: 'Am', scale: 'harmonic_minor' },
                rhythm: "'La Pompe' rhythm (Guitar).",
                harmony: "Minor/Diminished. Harmonic Minor.",
                voicing: "Acoustic guitar leads. Violin.",
                velocity: "Percussive strumming.",
                velocityStrategy: "accent_downbeat",
                melodyStrategy: ['virtuoso_solo', 'arpeggio'],
                logicTags: ["strum(fast)", "vibe(paris)"]
            },
            latin_jazz: {
                name: "Latin Jazz",
                defaults: { bpm: 120, key: 'F', scale: 'dorian' },
                rhythm: "Afro-Cuban 6/8 or 4/4.",
                harmony: "Montuno piano patterns.",
                voicing: "Clave based. Percussion heavy.",
                velocity: "Highly syncopated.",
                velocityStrategy: "groove",
                melodyStrategy: ['montuno', 'call_and_response'],
                logicTags: ["percussion(heavy)", "piano(montuno)"]
            },
            big_band: {
                name: "Big Band",
                defaults: { bpm: 150, key: 'Bb', scale: 'mixolydian' },
                rhythm: "Sectional playing.",
                harmony: "4-way close voicings.",
                voicing: "Shout choruses. Sax soli.",
                velocity: "Huge dynamic range.",
                velocityStrategy: "dynamic_swell",
                voicingStrategy: "drop2",
                melodyStrategy: ['call_and_response', 'shout_chorus'],
                logicTags: ["ensemble(large)", "hits(brass)"]
            },
            modal: {
                name: "Modal",
                defaults: { bpm: 130, key: 'Dm', scale: 'dorian' },
                harmonicRhythm: 'static',
                chordComplexity: 'jazz',
                improvisationProfile: { scaleDensity: 'medium', chromaticism: 'low' },
                rhythm: "Static harmony (1 chord/16 bars).",
                harmony: "Dorian/Phrygian.",
                voicing: "Pedal points. Color tones.",
                velocity: "Meditative.",
                velocityStrategy: "flat",
                voicingStrategy: "quartal",
                melodyStrategy: ['lyrical', 'motific'],
                logicTags: ["chords(static)", "improvisation(modal)"]
            },
            acid_jazz: {
                name: "Acid Jazz",
                defaults: { bpm: 95, key: 'D', scale: 'dorian' },
                rhythm: "Hip-hop loop beat.",
                harmony: "7th chords. Funk bass.",
                voicing: "Repetitive grooves. Samples.",
                velocity: "Loop-like consistency.",
                velocityStrategy: "flat",
                melodyStrategy: ['riff', 'groove'],
                logicTags: ["groove(loop)", "vibe(retro_mod)"]
            },
            smooth_jazz: {
                name: "Smooth Jazz",
                defaults: { bpm: 85, key: 'E', scale: 'pentatonic_major' },
                rhythm: "Pop production.",
                harmony: "Pentatonic/Blues. 'Safe' notes.",
                voicing: "Sax melody. Synthesizers.",
                velocity: "Polished, flat.",
                velocityStrategy: "flat",
                melodyStrategy: ['lyrical', 'riff'],
                logicTags: ["vibe(radio)", "sound(polished)"]
            },
            dixieland: {
                name: "Dixieland",
                defaults: { bpm: 190, key: 'F', scale: 'major' },
                rhythm: "2-beat feel (Oom-pah).",
                harmony: "Major triads. Polyphonic improv.",
                voicing: "Clarinet/Trumpet/Trombone weave.",
                velocity: "Happy, bouncy.",
                velocityStrategy: "bouncy",
                melodyStrategy: ['counterpoint', 'call_and_response'],
                logicTags: ["polyphony(improv)", "vibe(old_school)"]
            },
            ethio_jazz: {
                name: "Ethio-Jazz",
                defaults: { bpm: 100, key: 'G', scale: 'pentatonic_minor', timeSig: 6 },
                rhythm: "6/8 swing.",
                harmony: "Tezeta / Anchihoye scales.",
                voicing: "Vibrato-heavy horns.",
                velocity: "Hypnotic.",
                velocityStrategy: "soft_mist",
                melodyStrategy: ['pentatonic_flow', 'hypnotic'],
                logicTags: ["scale(exotic)", "mood(hypnotic)"]
            },
            third_stream: {
                name: "Third Stream",
                defaults: { bpm: 80, key: 'C', scale: 'chromatic' },
                rhythm: "Classical/Jazz blend.",
                harmony: "Classical forms, Jazz improv.",
                voicing: "Orchestral instruments.",
                velocity: "Intellectual.",
                velocityStrategy: "terraced",
                melodyStrategy: ['counterpoint', 'motific'],
                logicTags: ["style(hybrid)"]
            },
            soul_jazz: {
                name: "Soul Jazz",
                defaults: { bpm: 110, key: 'F', scale: 'blues' },
                rhythm: "Shuffle groove.",
                harmony: "Hammond B3 Organ riffs.",
                voicing: "Blues scale dominance.",
                velocity: "Greasy, laid back.",
                velocityStrategy: "groove",
                melodyStrategy: ['blues_licks', 'riff'],
                logicTags: ["instrument(organ)", "feel(blues)"]
            },
            afro_cuban: {
                name: "Afro-Cuban",
                defaults: { bpm: 130, key: 'C', scale: 'major', timeSig: 6 },
                rhythm: "Polyrhythmic (3 vs 2).",
                harmony: "Guajeo patterns.",
                voicing: "Bass tumbao (avoids the 1).",
                velocity: "Driving percussion.",
                velocityStrategy: "groove",
                melodyStrategy: ['montuno', 'call_and_response'],
                logicTags: ["bass(syncopated)", "rhythm(complex)"]
            },
            ragtime: {
                name: "Ragtime",
                defaults: { bpm: 90, key: 'C', scale: 'major' },
                rhythm: "Straight 1/16ths (Stride).",
                harmony: "Major. Secondary dominants.",
                voicing: "Stride Piano (Bass-Chord).",
                velocity: "Mechanical syncopation.",
                velocityStrategy: "accent_grid",
                melodyStrategy: ['ragtime_syncopation', 'riff'],
                logicTags: ["piano(stride)", "swing(none)"]
            },
            modern_jazz: {
                name: "Modern Jazz",
                defaults: { bpm: 120, key: 'F#', scale: 'augmented', timeSig: 5 },
                rhythm: "Complex meters (7/8).",
                harmony: "Non-functional harmony.",
                voicing: "Sparse, abstract.",
                velocity: "Highly dynamic.",
                velocityStrategy: "dynamic_swell",
                melodyStrategy: ['virtuoso_solo', 'motific'],
                logicTags: ["intellectual(high)", "harmony(abstract)"]
            }
        }
    },

    // -------------------------------------------------------------------------
    // ENSEMBLE 4: EDM KIT
    // Target Output: Synths, Drum Machines, FX, Bass.
    // -------------------------------------------------------------------------
    electronic: {
        name: "EDM Kit",
        ensembleConstraint: { min: 4, max: 8, requiredRoles: ['bass', 'percussion', 'melody'] },
        defaults: {
            mixProfile: {
                masterBus: {
                    compression: { threshold: -8, ratio: 6, attack: 0.001, release: 0.1 }, // Aggressive
                    eq: { low: 4, mid: -2, high: 4 }, // Hyped
                    width: 0.8 // Wide
                },
                globalReverb: { type: 'hall', decay: 1.5, preDelay: 0.05, wet: 0.25 },
                globalDelay: { delayTime: '8n', feedback: 0.5, wet: 0.3 }
            },
            bpm: 128,
            key: 'F',
            scale: 'minor',
            structure: [
                { type: 'INTRO', bars: 8 },
                { type: 'BUILD', bars: 8 },
                { type: 'DROP', bars: 8 },
                { type: 'BREAK', bars: 4 },
                { type: 'DROP', bars: 8 },
                { type: 'OUTRO', bars: 8 }
            ],
            harmonicRhythm: 'static',
            chordComplexity: 'triad',
            rubatoIntensity: 0.0,
            voiceLeadingStrictness: 'loose',
            doublingStrategy: 'power'
        },
        subGenres: {
            house: {
                name: "House",
                defaults: { bpm: 124, key: 'G', scale: 'minor' },
                rhythm: "4/4 'Four on floor'.",
                harmony: "Minor 7ths, 9ths.",
                voicing: "Piano stabs, Diva vocals.",
                velocity: "Swing 50-60%.",
                velocityStrategy: "accent_downbeat",
                melodyStrategy: ['riff', 'motific', 'call_and_response'],
                logicTags: ["bpm(120-128)", "kick(4/4)"]
            },
            techno: {
                name: "Techno",
                defaults: { bpm: 135, key: 'F', scale: 'phrygian' },
                rhythm: "4/4 Industrial.",
                harmony: "Phrygian/Locrian. One note bass.",
                voicing: "Percussive synths.",
                velocity: "Rigid, machine-like.",
                velocityStrategy: "flat",
                melodyStrategy: ['ostinato', 'minimal'],
                logicTags: ["bpm(130-140)", "mood(dark)"]
            },
            trance: {
                name: "Trance",
                defaults: { bpm: 138, key: 'A', scale: 'minor' },
                harmonicRhythm: 'slow_burn',
                chordComplexity: 'extended',
                rhythm: "Rolling 1/16th basslines.",
                harmony: "Minor/Major. Epic arpeggios.",
                voicing: "Supersaws. Huge pads.",
                velocity: "High energy builds.",
                velocityStrategy: "crescendo",
                melodyStrategy: ['arpeggio', 'anthem'],
                logicTags: ["arpeggio(fast)", "build(long)"]
            },
            dubstep: {
                name: "Dubstep",
                defaults: { bpm: 140, key: 'F', scale: 'minor' }, // Halftime 140
                rhythm: "Half-time feel (140bpm).",
                harmony: "Minor. Wobble bass.",
                voicing: "Sub-bass focus. Pitch bends.",
                velocity: "Heavy drop.",
                velocityStrategy: "heavy",
                melodyStrategy: ['wobble', 'drop'],
                logicTags: ["bass(wobble)", "rhythm(halftime)"]
            },
            dnb: {
                name: "DnB",
                defaults: { bpm: 174, key: 'F', scale: 'minor' },
                rhythm: "Fast breakbeats (170bpm) Break.",
                harmony: "Jazz chords or Dark minor.",
                voicing: "Reese bass. Chopped breaks.",
                velocity: "Fast, frantic.",
                velocityStrategy: "fixed_max",
                melodyStrategy: ['fast_riff', 'pad'],
                logicTags: ["bpm(170+)", "breakbeat(complex)"]
            },
            synthwave: {
                name: "Synthwave",
                defaults: { bpm: 95, key: 'C', scale: 'natural_minor' },
                rhythm: "Straight 1/8th pulse.",
                harmony: "Natural Minor. Sus2 chords.",
                voicing: "Arpeggiated bass. Analog leads.",
                velocity: "Consistent 80s feel.",
                velocityStrategy: "flat",
                melodyStrategy: ['lyrical', 'arpeggio'],
                logicTags: ["vibe(80s)", "bass(arp)"]
            },
            trap: {
                name: "Trap",
                defaults: { bpm: 145, key: 'C#', scale: 'phrygian' },
                rhythm: "808 Bass. Rolling hats.",
                harmony: "Phrygian/Minor. Simple loops.",
                voicing: "High bells. Sub bass.",
                velocity: "Hat rolls (1/32, 1/64).",
                velocityStrategy: "dynamic_swell", // For hat rolls
                melodyStrategy: ['riff', 'minimal'],
                logicTags: ["hihats(rolls)", "bass(808)"]
            },
            lofi: {
                name: "Lo-Fi",
                defaults: { bpm: 80, key: 'Db', scale: 'major' },
                rhythm: "Drunken swing.",
                harmony: "Jazzy. Sampled loops.",
                voicing: "Degraded audio. Piano.",
                velocity: "Soft, uneven.",
                velocityStrategy: "soft_mist",
                melodyStrategy: ['lyrical', 'relaxed'],
                logicTags: ["swing(heavy)", "sound(degraded)"]
            },
            idm: {
                name: "IDM",
                defaults: { bpm: 110, key: 'G', scale: 'chromatic' },
                rhythm: "Glitchy, irregular.",
                harmony: "Atonal or Ambient.",
                voicing: "Micro-edits. Granular.",
                velocity: "Random probability.",
                velocityStrategy: "random_spike",
                melodyStrategy: ['random_spike', 'glitch'],
                logicTags: ["glitch(on)", "rhythm(random)"]
            },
            future_bass: {
                name: "Future Bass",
                defaults: { bpm: 150, key: 'F', scale: 'major' },
                harmonicRhythm: 'moderate',
                chordComplexity: 'extended',
                rhythm: "Halftime.",
                harmony: "Major 7ths. LFO chords.",
                voicing: "'Wub' chords. Vocal chops.",
                velocity: "Sidechained heavily.",
                velocityStrategy: "dynamic_swell",
                melodyStrategy: ['riff', 'wobble'],
                logicTags: ["chords(wub)", "sidechain(heavy)"]
            },
            ambient: {
                name: "Ambient",
                defaults: { bpm: 60, key: 'C', scale: 'lydian' },
                rhythm: "No grid. Drone.",
                harmony: "Major/Lydian.",
                voicing: "Washes, pads, field recordings.",
                velocity: "Slow attack.",
                velocityStrategy: "soft_mist",
                melodyStrategy: ['slow_swell', 'drone'],
                logicTags: ["attack(slow)", "beat(none)"]
            },
            deep_house: {
                name: "Deep House",
                defaults: { bpm: 122, key: 'Am', scale: 'minor' },
                rhythm: "Slow 4/4.",
                harmony: "Minor 9ths. Smooth pads.",
                voicing: "Muted bass. Soulful vocals.",
                velocity: "Smooth, low-pass filtered.",
                velocityStrategy: "soft_mist",
                melodyStrategy: ['minimal', 'riff'],
                logicTags: ["filter(lowpass)", "mood(sexy)"]
            },
            hardstyle: {
                name: "Hardstyle",
                defaults: { bpm: 150, key: 'G', scale: 'minor' },
                rhythm: "Distorted Kick (Bong-Bong).",
                harmony: "Minor. Anthemic melody.",
                voicing: "Detuned leads.",
                velocity: "Max velocity.",
                velocityStrategy: "fixed_max",
                melodyStrategy: ['riff', 'anthem'],
                logicTags: ["kick(distorted)", "energy(max)"]
            },
            garage: {
                name: "Garage",
                defaults: { bpm: 132, key: 'F', scale: 'minor' },
                rhythm: "2-Step (shuffle).",
                harmony: "Minor 7ths.",
                voicing: "Chopped vocals. Organ bass.",
                velocity: "Swing 60%+.",
                velocityStrategy: "swing_accents",
                melodyStrategy: ['riff', 'vocal_chops'],
                logicTags: ["rhythm(2step)", "swing(heavy)"]
            },
            acid: {
                name: "Acid",
                defaults: { bpm: 130, key: 'C', scale: 'phrygian' },
                rhythm: "4/4.",
                harmony: "Minor.",
                voicing: "303 squelch. Resonant sweeps.",
                velocity: "Accent patterns.",
                velocityStrategy: "accent_grid",
                melodyStrategy: ['ostinato', 'arpeggio'],
                logicTags: ["synth(303)", "resonance(high)"]
            },
            chiptune: {
                name: "Chiptune",
                defaults: { bpm: 160, key: 'C', scale: 'major' },
                rhythm: "Fast, rigid.",
                harmony: "Major/Minor.",
                voicing: "Square/Pulse waves. Arps.",
                velocity: "Mechanical.",
                velocityStrategy: "flat",
                melodyStrategy: ['arpeggio', 'riff'],
                logicTags: ["sound(8bit)", "arp(fast)"]
            },
            phonk: {
                name: "Phonk",
                defaults: { bpm: 120, key: 'C#', scale: 'minor' }, // Memphis rap style
                rhythm: "Memphis Rap tapes.",
                harmony: "Minor. Cowbells.",
                voicing: "Distorted vocals. 808s.",
                velocity: "Gritty.",
                velocityStrategy: "heavy",
                melodyStrategy: ['riff', 'loop'],
                logicTags: ["cowbell(heavy)", "vibe(memphis)"]
            },
            jungle: {
                name: "Jungle",
                defaults: { bpm: 165, key: 'F', scale: 'minor' },
                rhythm: "Amen Break.",
                harmony: "Reggae chords.",
                voicing: "Deep sub. Sample chops.",
                velocity: "Complex breaks.",
                velocityStrategy: "fixed_max",
                melodyStrategy: ['fast_riff', 'pad'],
                logicTags: ["breakbeat(amen)", "bass(reggae)"]
            },
            electro: {
                name: "Electro",
                defaults: { bpm: 128, key: 'A', scale: 'phrygian' },
                rhythm: "Robotic funk.",
                harmony: "Minor.",
                voicing: "Vocoder. Zap drums.",
                velocity: "Stiff funk.",
                velocityStrategy: "flat",
                melodyStrategy: ['robotic', 'riff'],
                logicTags: ["vocoder(on)", "beat(808)"]
            },
            disco: {
                name: "Disco",
                defaults: { bpm: 120, key: 'D', scale: 'mixolydian' },
                rhythm: "4/4. Octave bass.",
                harmony: "Major 7ths. Strings.",
                voicing: "Funky guitar. Brass hits.",
                velocity: "Upbeat, party.",
                velocityStrategy: "accent_downbeat",
                melodyStrategy: ['riff', 'call_and_response'],
                logicTags: ["bass(octave)", "strings(disco)"]
            }
        }
    },

    // -------------------------------------------------------------------------
    // ENSEMBLE 5: WORLD ENSEMBLE
    // Target Output: Traditional instruments, culturally specific scales.
    // -------------------------------------------------------------------------
    world: {
        name: "World Ensemble",
        ensembleConstraint: { min: 3, max: 6, requiredRoles: ['percussion'] },
        defaults: {
            mixProfile: {
                masterBus: {
                    compression: { threshold: -14, ratio: 3, attack: 0.02, release: 0.2 },
                    eq: { low: 1, mid: 0, high: 1 },
                    width: 0.7
                },
                globalReverb: { type: 'hall', decay: 2.0, preDelay: 0.03, wet: 0.25 },
                globalDelay: { delayTime: '4n.', feedback: 0.4, wet: 0.15 }
            },
            bpm: 100,
            key: 'D',
            scale: 'major',
            structure: [
                { type: 'INTRO', bars: 4 },
                { type: 'GROOVE_A', bars: 8 },
                { type: 'GROOVE_B', bars: 8 },
                { type: 'OUTRO', bars: 4 }
            ],
            voiceLeadingStrictness: 'loose',
            doublingStrategy: 'classic'
        },
        subGenres: {
            salsa: {
                name: "Salsa",
                defaults: { bpm: 180, key: 'F', scale: 'major' },
                rhythm: "Clave (2-3 or 3-2).",
                harmony: "Montuno piano. Major/Minor.",
                voicing: "Brass sections. Percussion layers.",
                velocity: "Highly syncopated.",
                velocityStrategy: "groove",
                melodyStrategy: ['call_and_response', 'montuno'],
                logicTags: ["rhythm(clave)", "piano(montuno)"]
            },
            reggae: {
                name: "Reggae",
                defaults: { bpm: 75, key: 'G', scale: 'major' },
                rhythm: "Skank on 2 and 4.",
                harmony: "Major. Simple triads.",
                voicing: "Organ bubbles. One-drop drum.",
                velocity: "Laid back.",
                velocityStrategy: "accent_offbeat",
                melodyStrategy: ['riff', 'lyrical'],
                logicTags: ["accent(offbeat)", "bass(heavy)"]
            },
            afrobeat: {
                name: "Afrobeat",
                defaults: { bpm: 120, key: 'F', scale: 'mixolydian' },
                rhythm: "Complex polyrhythm.",
                harmony: "Modal (Dorian/Mixolydian).",
                voicing: "Horn section loops. Guitar picking.",
                velocity: "Relentless groove.",
                velocityStrategy: "groove",
                melodyStrategy: ['call_and_response', 'riff'],
                logicTags: ["groove(long)", "horns(repetitive)"]
            },
            celtic: {
                name: "Celtic",
                defaults: { bpm: 110, key: 'D', scale: 'dorian', timeSig: 6 },
                rhythm: "Jig (6/8) or Reel (4/4).",
                harmony: "Mixolydian/Dorian.",
                voicing: "Fiddle/Flute melody. Drone.",
                velocity: "Bouncy, constant flow.",
                velocityStrategy: "bouncy",
                melodyStrategy: ['lyrical', 'ornamented'],
                logicTags: ["instrument(fiddle)", "ornament(grace)"]
            },
            tango: {
                name: "Tango",
                defaults: { bpm: 120, key: 'Gm', scale: 'harmonic_minor' },
                rhythm: "Staccato 4/4 (Habanera).",
                harmony: "Harmonic Minor.",
                voicing: "Accordion (Bandoneon).",
                velocity: "Sharp attacks, dramatic pauses.",
                velocityStrategy: "random_spike", // Sharp attacks
                melodyStrategy: ['staccato', 'dramatic'],
                logicTags: ["articulation(staccato)", "drama(high)"]
            },
            flamenco: {
                name: "Flamenco",
                defaults: { bpm: 140, key: 'E', scale: 'phrygian_dominant' }, // Spanish Phrygian
                rhythm: "12-beat cycle (Compás).",
                harmony: "Phrygian Dominant.",
                voicing: "Nylon Guitar rasgueado.",
                velocity: "Aggressive strumming.",
                velocityStrategy: "heavy",
                melodyStrategy: ['virtuoso_solo', 'strum'],
                logicTags: ["guitar(spanish)", "rhythm(12beat)"]
            },
            indian_raga: {
                name: "Indian Raga",
                defaults: { bpm: 90, key: 'C#', scale: 'bhairav' },
                rhythm: "Drone based. No chords.",
                harmony: "Specific Ragas (Scales).",
                voicing: "Sitar/Tabla. Microtonal bends.",
                velocity: "Fluid acceleration (Jhala).",
                velocityStrategy: "crescendo",
                melodyStrategy: ['virtuoso_solo', 'drone'],
                logicTags: ["harmony(drone)", "bends(microtonal)"]
            },
            bossa_nova_world: {
                name: "Bossa Nova (World)",
                defaults: { bpm: 130, key: 'C', scale: 'major' },
                rhythm: "Gentle Samba.",
                harmony: "Major 7/9.",
                voicing: "Nylon guitar. Whisper vocals.",
                velocity: "Soft, intimate.",
                velocityStrategy: "soft_mist",
                melodyStrategy: ['lyrical', 'motif'],
                logicTags: ["mood(chill)", "rhythm(brazil)"]
            },
            gamelan: {
                name: "Gamelan",
                defaults: { bpm: 110, key: 'C', scale: 'pelog' },
                rhythm: "Cycles (Gong).",
                harmony: "Pelog / Slendro scales.",
                voicing: "Metallophones. Interlocking parts.",
                velocity: "Bell-like, resonant.",
                velocityStrategy: "flat",
                melodyStrategy: ['cyclic', 'ostinato'],
                logicTags: ["texture(metallic)", "tuning(pelog)"]
            },
            klezmer: {
                name: "Klezmer",
                defaults: { bpm: 135, key: 'Cm', scale: 'phrygian_dominant' },
                rhythm: "Um-pa accompaniment.",
                harmony: "Freygish (Phrygian Dominant).",
                voicing: "Clarinet/Violin wailing.",
                velocity: "Laughing/Crying articulations.",
                velocityStrategy: "bouncy",
                melodyStrategy: ['virtuoso_solo', 'ornamented'],
                logicTags: ["mood(celebration)", "scale(jewish)"]
            },
            polka: {
                name: "Polka",
                defaults: { bpm: 140, key: 'F', scale: 'major', timeSig: 2 },
                rhythm: "Fast 2/4.",
                harmony: "Major I-V.",
                voicing: "Accordion/Tuba.",
                velocity: "Upbeat, hopping.",
                velocityStrategy: "accent_downbeat",
                melodyStrategy: ['motif', 'oompah'],
                logicTags: ["tempo(fast)", "feel(oompah)"]
            },
            calypso: {
                name: "Calypso",
                defaults: { bpm: 115, key: 'C', scale: 'major' },
                rhythm: "Syncopated 4/4.",
                harmony: "Major.",
                voicing: "Steel Drums.",
                velocity: "Bright, tropical.",
                velocityStrategy: "bouncy",
                melodyStrategy: ['riff', 'syncopated'],
                logicTags: ["instrument(steeldrum)", "vibe(island)"]
            },
            middle_east: {
                name: "Middle East",
                defaults: { bpm: 100, key: 'D', scale: 'harmonic_minor' }, // Approx for Maqam
                rhythm: "Maqam specific.",
                harmony: "Quarter tones.",
                voicing: "Oud/Qanun tremolo.",
                velocity: "Ornamented melody.",
                velocityStrategy: "flat",
                melodyStrategy: ['ornamented', 'maqam'],
                logicTags: ["scale(quartertone)", "instrument(oud)"]
            },
            andean: {
                name: "Andean",
                defaults: { bpm: 90, key: 'G', scale: 'pentatonic_minor' },
                rhythm: "Pentatonic.",
                harmony: "Minor/Major relative.",
                voicing: "Pan flutes (Siku). Charango.",
                velocity: "Breathy, fluttering.",
                velocityStrategy: "soft_mist",
                melodyStrategy: ['lyrical', 'flute'],
                logicTags: ["instrument(panflute)", "vibe(mountain)"]
            },
            samba: {
                name: "Samba",
                defaults: { bpm: 145, key: 'D', scale: 'mixolydian' },
                rhythm: "Fast 2/4. Surdo beats.",
                harmony: "Major 6/9 chords.",
                voicing: "Batucada percussion.",
                velocity: "High energy carnival.",
                velocityStrategy: "fixed_max",
                melodyStrategy: ['rhythmic', 'call_and_response'],
                logicTags: ["percussion(max)", "tempo(fast)"]
            },
            dancehall: {
                name: "Dancehall",
                defaults: { bpm: 95, key: 'Am', scale: 'minor' },
                rhythm: "Dem Bow rhythm.",
                harmony: "Minor. Simple chords.",
                voicing: "Sub bass. MC rhythm.",
                velocity: "Heavy kick/snare.",
                velocityStrategy: "heavy",
                melodyStrategy: ['riff', 'rhythmic'],
                logicTags: ["rhythm(dembow)", "bass(sub)"]
            },
            bluegrass: {
                name: "Bluegrass",
                defaults: { bpm: 150, key: 'G', scale: 'major', timeSig: 2 }, // Cut time
                rhythm: "Fast cut time.",
                harmony: "Major I-IV-V.",
                voicing: "Banjo rolls. Fiddle.",
                velocity: "Driving, ahead of beat.",
                velocityStrategy: "fixed_max",
                melodyStrategy: ['virtuoso_solo', 'riff'],
                logicTags: ["instrument(banjo)", "tempo(fast)"]
            },
            highlife: {
                name: "Highlife",
                defaults: { bpm: 110, key: 'C', scale: 'major' },
                rhythm: "Jazzy Horns + African beat.",
                harmony: "Major Diatonic.",
                voicing: "Palm wine guitar.",
                velocity: "Joyful, lilting.",
                velocityStrategy: "bouncy",
                melodyStrategy: ['riff', 'call_and_response'],
                logicTags: ["guitar(clean)", "mood(happy)"]
            },
            mariachi: {
                name: "Mariachi",
                defaults: { bpm: 120, key: 'F', scale: 'major', timeSig: 3 },
                rhythm: "Waltz or Polka feel.",
                harmony: "Major. 3rds/6ths harmony.",
                voicing: "Trumpets + Violins. Guitarron.",
                velocity: "Belting, bold.",
                velocityStrategy: "accent_downbeat",
                melodyStrategy: ['lyrical', 'harmonized'],
                logicTags: ["style(mexican)", "instrument(trumpet)"]
            },
            zouk: {
                name: "Zouk",
                defaults: { bpm: 100, key: 'F', scale: 'major' },
                rhythm: "Zouk beat (Caribbean).",
                harmony: "Major/Minor.",
                voicing: "Synthesizers + Guitar.",
                velocity: "Flowing, danceable.",
                velocityStrategy: "groove",
                melodyStrategy: ['riff', 'rhythmic'],
                logicTags: ["rhythm(zouk)", "mood(party)"]
            }
        }
    }
};
